import axios from 'axios';
import baseURL from '../../services/apiBaseUrl';

const PERSONAL_TOKEN_KEY = 'mishtameretPortalToken';
const STATION_TOKEN_KEY = 'mishtameretStationToken';

/** @type {'personal' | 'station'} */
let sessionMode = 'personal';

export function setPortalSessionMode(mode) {
  sessionMode = mode === 'station' ? 'station' : 'personal';
}

export function getPortalSessionMode() {
  return sessionMode;
}

function tokenKey() {
  return sessionMode === 'station' ? STATION_TOKEN_KEY : PERSONAL_TOKEN_KEY;
}

export function getMishtameretToken() {
  const key = tokenKey();
  if (sessionMode === 'station') {
    return sessionStorage.getItem(key);
  }
  return sessionStorage.getItem(key) || localStorage.getItem(key);
}

/** @param {string|null} token @param {{ persist?: boolean }} [opts] */
export function setMishtameretToken(token, opts = {}) {
  const key = tokenKey();
  if (sessionMode === 'station') {
    sessionStorage.removeItem(STATION_TOKEN_KEY);
    if (token) sessionStorage.setItem(STATION_TOKEN_KEY, token);
    return;
  }
  const persist = Boolean(opts.persist);
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
  if (!token) return;
  if (persist) localStorage.setItem(key, token);
  else sessionStorage.setItem(key, token);
}

export function clearMishtameretSession() {
  if (sessionMode === 'station') {
    sessionStorage.removeItem(STATION_TOKEN_KEY);
    return;
  }
  sessionStorage.removeItem(PERSONAL_TOKEN_KEY);
  localStorage.removeItem(PERSONAL_TOKEN_KEY);
}

const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getMishtameretToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      clearMishtameretSession();
    }
    return Promise.reject(err);
  }
);

/**
 * @param {string} phone
 * @param {string} idNumber
 * @param {boolean} [rememberMe]
 * @param {{ station?: boolean }} [opts]
 */
export async function mishtameretLogin(phone, idNumber, rememberMe = false, opts = {}) {
  const station = Boolean(opts.station);
  setPortalSessionMode(station ? 'station' : 'personal');
  const { data } = await api.post('/mishtameret-portal/login', {
    phone,
    idNumber,
    rememberMe: station ? false : Boolean(rememberMe),
    station,
  });
  setMishtameretToken(data.token, { persist: station ? false : Boolean(rememberMe) });
  return data;
}

/** טופס רישום פתוח — יוצר בקשה ממתינה לאישור המשרד */
export async function mishtameretRegister(payload) {
  const { data } = await api.post('/mishtameret-portal/register', payload);
  return data;
}

export async function fetchMe() {
  const { data } = await api.get('/mishtameret-portal/me');
  return data;
}

export async function updateMyEmail(email) {
  const { data } = await api.put('/mishtameret-portal/me/email', { email });
  return data;
}

export async function sendPortalFeedback(payload) {
  const { data } = await api.post('/mishtameret-portal/feedback', payload);
  return data;
}

export async function fetchExams() {
  const { data } = await api.get('/mishtameret-portal/exams');
  return data;
}

/** חיפוש במבחנים הקודמים (מלפני המערכת) — לפי מספר מבחן או מסכת */
export async function searchArchiveExams(q, frame) {
  const { data } = await api.get('/mishtameret-portal/exams/archive', { params: { q, frame } });
  return data;
}

export async function startAttempt(examId) {
  const { data } = await api.post(`/mishtameret-portal/exams/${examId}/attempts`);
  return data;
}

export async function fetchAttempt(attemptId) {
  const { data } = await api.get(`/mishtameret-portal/attempts/${attemptId}`);
  return data;
}

export async function submitAnswer(attemptId, globalIndex, chosenDisplayKey) {
  const { data } = await api.post(`/mishtameret-portal/attempts/${attemptId}/answer`, {
    globalIndex,
    chosenDisplayKey,
  });
  return data;
}

export async function finishAttempt(attemptId) {
  const { data } = await api.post(`/mishtameret-portal/attempts/${attemptId}/finish`);
  return data;
}

/** הזמנת כרך גמרא בסיום מבחן — { available, ordered, campaign: { id, title, closesAt } | null } */
export async function fetchFrameGemaraOrder(frame) {
  const { data } = await api.get('/mishtameret-portal/gemara-order', { params: { frame } });
  return data;
}

export async function fetchGemaraOrder(attemptId) {
  const { data } = await api.get(`/mishtameret-portal/attempts/${attemptId}/gemara-order`);
  return data;
}

export async function orderGemara(attemptId) {
  const { data } = await api.post(`/mishtameret-portal/attempts/${attemptId}/gemara-order`);
  return data;
}

export async function fetchGrades() {
  const { data } = await api.get('/mishtameret-portal/grades');
  return data;
}

/** לוח השנה — כל תוכנית הלימודים: { today, range, days } */
export async function fetchStudyCalendar() {
  const { data } = await api.get('/mishtameret-portal/calendar');
  return data;
}

/** סיור במערכת הטלפונית — { root, globalKeys }: עץ השלוחות עם מה שיש בכל אחת עכשיו */
export async function fetchPhoneTour() {
  const { data } = await api.get('/mishtameret-portal/phone-tour');
  return data;
}

export function phoneTourPromptAudioPath(code) {
  return `/mishtameret-portal/phone-tour/prompts/${code}/audio`;
}

export async function fetchLibraryChannels() {
  const { data } = await api.get('/mishtameret-portal/library/channels');
  return data;
}

export async function fetchLibraryLessons(channel) {
  const { data } = await api.get('/mishtameret-portal/library/lessons', {
    params: { channel },
  });
  return data;
}

/** Fetch media with auth → blob URL. Lesson streams get audio/* when missing. */
export async function fetchAudioBlobUrl(path) {
  const clean = path.startsWith('/') ? path.replace(/^\/api/, '') : path;
  const url = clean.startsWith('/mishtameret-portal')
    ? clean
    : `/mishtameret-portal${clean.startsWith('/') ? '' : '/'}${clean}`;
  const { data } = await api.get(url, { responseType: 'blob' });
  const isLessonStream = /\/library\/lessons\/[^/]+\/stream/.test(url);
  const typed = !isLessonStream || (data?.type && data.type.startsWith('audio/'))
    ? data
    : new Blob([data], { type: 'audio/wav' });
  return URL.createObjectURL(typed);
}

export async function downloadLessonFile(lessonId, filename = 'shiur.wav') {
  const { data } = await api.get(
    `/mishtameret-portal/library/lessons/${lessonId}/download`,
    { responseType: 'blob' }
  );
  const href = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export function lessonStreamPath(lessonId) {
  return `/mishtameret-portal/library/lessons/${lessonId}/stream`;
}

export function channelProfileImagePath(channel) {
  return `/mishtameret-portal/library/channels/${encodeURIComponent(channel)}/profile-image`;
}

export default api;
